import React, { useState, useEffect } from 'react';
import Modal, { ModalBody, ModalHeader, ModalTitle, ModalFooter } from '../../../../components/bootstrap/Modal';
import Button from '../../../../components/bootstrap/Button';
import Checks from '../../../../components/bootstrap/forms/Checks';
import Select from '../../../../components/bootstrap/forms/Select';
import Textarea from '../../../../components/bootstrap/forms/Textarea';
import FormGroup from '../../../../components/bootstrap/forms/FormGroup';
import Label from '../../../../components/bootstrap/forms/Label';
import showNotification from '../../../../components/extras/showNotification';
import BillingValuesDB from '../../../../database/wrappers/billingValues';
import api from '../../../../services/api';

const ReportModal = ({ isOpen, setIsOpen, data, onSuccess }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectedItems, setSelectedItems] = useState([]);
  const [problemType, setProblemType] = useState('cobranca_indevida');
  const [justification, setJustification] = useState('');

  useEffect(() => {
    if (isOpen && data) {
      fetchValues();
      setJustification('');
      setSelectedItems([]);
      setProblemType('cobranca_indevida');
    }
  }, [isOpen, data]);

    const fetchValues = async () => {
    try {
      setLoading(true);
      const db = new BillingValuesDB();
      const billingValues = await db.get('BillingValues');

      if (!data || !data.items) {
          setItems([]);
          return;
      }

      const newItems = [
        {
          id: 'system',
          name: 'Sistema',
          price: (billingValues?.system || 0) / 100,
          quantity: data?.items?.system?.qtdInitial || 0
        },
        {
          id: 'pos',
          name: 'POS',
          price: (billingValues?.pos || 0) / 100,
          quantity: data?.items?.pos?.qtdInitial || 0
        },
        {
          id: 'posDelivery',
          name: 'POS Delivery',
          price: (billingValues?.posDelivery || 0) / 100,
          quantity: data?.items?.posDelivery?.qtdInitial || 0
        },
        {
          id: 'printer',
          name: 'Impressora',
          price: (billingValues?.printer || 0) / 100,
          quantity: data?.items?.printer?.qtdInitial || 0
        },
        {
          id: 'totem',
          name: 'Totem',
          price: (billingValues?.totem || 0) / 100,
          quantity: data?.items?.totem?.qtdInitial || 0
        },
        {
          id: 'tablet',
          name: 'Tablet',
          price: (billingValues?.tablet || 0) / 100,
          quantity: data?.items?.tablet?.qtdInitial || 0
        },
      ];
      setItems(newItems);
    } catch (error) {
      console.error(error);
      showNotification('Erro', 'Erro ao carregar valores de referência', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const handleSelectItem = (id) => {
    if (selectedItems.includes(id)) {
      setSelectedItems(selectedItems.filter(item => item !== id));
    } else {
      setSelectedItems([...selectedItems, id]);
    }
  };

  const total = items.reduce((acc, item) => acc + (item.price * item.quantity), 0);

  const handleConfirm = async () => {
    if (!justification.trim()) {
      showNotification('Atenção', 'A justificativa é obrigatória', 'warning');
      return;
    }

    try {
      setLoading(true);

      const itemsPayload = items.reduce((acc, item) => {
        acc[item.id] = selectedItems.includes(item.id) ? item.quantity : 0;
        return acc;
      }, {});
      const payload = {
        billingId: data.id,
        justification: justification,
        reason: problemType,
        items: itemsPayload
      };

      await api.put('billing/report', payload);

      showNotification(
        'Sucesso',
        'Problema reportado com sucesso',
        'success'
      );
      if (onSuccess) {
        onSuccess();
      }
      setIsOpen(false);
    } catch (error) {
      showNotification('Erro', 'Erro ao reportar problema', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const problemOptions = [
    { value: 'cobranca_indevida', text: 'Cobrança indevida' },
    { value: 'servico_nao_prestado', text: 'Serviço não prestado' },
    { value: 'outro', text: 'Outro' },
  ];

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      titleId="report-problem-modal"
      isCentered
      size="lg"
    >
      <ModalHeader className="p-4" setIsOpen={setIsOpen}>
        <ModalTitle id="report-problem-modal-title">Reportar problema</ModalTitle>
      </ModalHeader>
      <ModalBody>
        <div className="row g-2">
          <div className="col-12">
            <FormGroup id="problemType" label="Selecione o problema">
              <Select
                ariaLabel="Selecione o problema"
                list={problemOptions}
                value={problemType}
                onChange={(e) => setProblemType(e.target.value)}
                disabled={loading}
              />
            </FormGroup>
          </div>

          <div className="col-12">
            <Label>Selecione o produto</Label>
            <div className="table-responsive">
              <table className="table table-borderless align-middle mb-0">
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id}>
                      <td style={{ width: '5%' }}>
                        <Checks
                          id={`item-${item.id}`}
                          name={`item-${item.id}`}
                          checked={selectedItems.includes(item.id)}
                          onChange={() => handleSelectItem(item.id)}
                          disabled={loading}
                        />
                      </td>
                      <td style={{ width: '35%' }}>{item.name}</td>
                      <td style={{ width: '20%' }}>{formatCurrency(item.price)}</td>
                      <td style={{ width: '20%', textAlign: 'center' }}>
                        {String(item.quantity).padStart(2, '0')}
                      </td>
                      <td style={{ width: '20%', textAlign: 'right' }}>
                        {formatCurrency(item.price * item.quantity)}
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td colSpan="2" className="text-start fw-bold pt-2">Total</td>
                    <td colSpan="3" className="text-end fw-bold pt-2">{formatCurrency(total)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="col-12">
            <FormGroup id="justification" label="Justifique o problema*">
              <Textarea
                rows={2}
                placeholder="Insira a justificativa do problema"
                value={justification}
                onChange={(e) => setJustification(e.target.value)}
                disabled={loading}
              />
            </FormGroup>
          </div>
        </div>
      </ModalBody>
      <ModalFooter className="p-4">
        <Button icon="Close" color="danger" onClick={() => setIsOpen(false)} disabled={loading}>Cancelar</Button>
        <Button icon="Check" color="success" onClick={handleConfirm} disabled={loading}>
          {loading ? 'Enviando...' : 'Confirmar Alterações'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ReportModal;
